import React, {useState, useRef, useCallback} from 'react'
import {Box} from 'jsxstyle'
import classNames from 'classnames'
import RichInput, {RichInputRef} from '../RichInput'
import {StyledFormField} from '../FormField/FormField'
import {useDebouncedCallback} from 'use-debounce'

import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import RichTextToolbar from '../RichTextToolbar'

interface FormInputProps {
  value: string
  onSubmit: (value: string) => void
  minHeight?: string
  maxHeight?: string
  debounce?: number
  placeholder?: string
  readOnly?: boolean
  className?: string
  error?: string
  richTextEnabled?: boolean
}

const FormInput: React.FC<FormInputProps> = ({
  value,
  placeholder,
  minHeight = '200px',
  maxHeight = '300px',
  readOnly = false,
  className,
  onSubmit,
  error,
  debounce = 1000,
  richTextEnabled = false,
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const richRef = useRef<RichInputRef>(null)

  const debouncedOnSubmit = useDebouncedCallback((html: string) => {
    onSubmit(html)
  }, debounce)

  const exec = useCallback((fn: (editor: RichInputRef['editor']) => void) => {
    const editor = richRef.current?.editor
    if (editor) {
      fn(editor)
      editor.chain().focus().run()
    }
  }, [])

  return (
    <Box marginTop="8px" marginBottom="4px">
      <StyledFormField className={classNames({withError: !!error})}>
        {richTextEnabled && <RichTextToolbar richRef={richRef} exec={exec} />}

        <RichInput
          ref={richRef}
          value={value}
          readOnly={readOnly}
          placeholder={placeholder}
          className={className}
          style={{
            border: isFocused ? '1px solid #0E78F5' : '1px solid #eeeeee',
            borderRadius: '6px',
            padding: '10px',
            minHeight,
            maxHeight,
            overflowY: 'auto',
            transition: 'border-color 0.2s ease',
            backgroundColor: '#FFFFFF',
            outline: 'none',
            fontSize: 'var(--default-font-size)',
            fontWeight: 400,
            color: '#373c45',
            boxSizing: 'border-box',
          }}
          extensions={richTextEnabled ? [TextStyle, Color] : []}
          onChange={(html) => debouncedOnSubmit(html)}
          onFocus={() => setIsFocused(true)}
          onEditorBlur={(html) => {
            setIsFocused(false)
            onSubmit(html)
          }}
        />

        {error && <div className="field-error">{error}</div>}
      </StyledFormField>
    </Box>
  )
}

export default FormInput
